import * as d3 from 'd3';
import { ChartRenderer } from './ChartRenderer';
import { ChartData } from '../data/ChartData';
import { DonutChartConfig } from '../data/DonutChartConfig';
import { ChartTooltipData } from './ChartTooltipData';
import { MeasureUnitInfo } from 'components/measure-unit/MeasureUnitInfo';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';

export class DonutChartRenderer extends ChartRenderer {
  private paddingForLabels: number = 0;
  private data: Array<d3.PieArcDatum<ChartData>>;

  constructor(protected config: DonutChartConfig) {
    super();
    const pie = d3
      .pie<ChartData>()
      .value((d: ChartData) => d.value!)
      .sort(null); //set sorting to null to avoid 'spacer' (isTransparent = true) at the end to be reordered (we ordered everything anyways)

    // taking the first data only
    this.data = pie(this.config.data![0].data);
  }

  private wrapLabel(label: string, maxLength: number = 30): string[] {
    if (label.length <= maxLength) {
      return [label];
    }

    const words = label.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;

      if (testLine.length <= maxLength) {
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          lines.push(word.substring(0, maxLength));
          currentLine = word.substring(maxLength);
        }
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines;
  }

  private drawLabelsWithLines(radius: number, donutArc: d3.Arc<any, d3.DefaultArcObject>): void {
    const valuesTotal = d3.sum(this.data, function (d) {
      return d.value;
    });
    const labelArc = d3
      .arc()
      .outerRadius(radius * 0.99)
      .innerRadius(radius * 0.99);

    // draw Labels
    this.gElement!.append('g')
      .attr('class', 'donut-labels')
      .selectAll('text')
      .data(this.data)
      .enter()
      .append('text')
      .html((d: d3.PieArcDatum<ChartData>) => {
        const percentage = Math.round((100 * d.data.value!) / valuesTotal);
        const wrappedLines = this.wrapLabel(d.data.label || '');

        let html = `<tspan x='0' style='font-weight: bold'>${percentage}%</tspan>`;

        wrappedLines.forEach((line, index) => {
          html += `<tspan x='0' dy='${index === 0 ? '1em' : '1.2em'}'>${line}</tspan>`;
        });

        return html;
      })
      .attr('class', 'donut-label')
      .attr('transform', (d: d3.PieArcDatum<ChartData>): string => {
        const pos = labelArc.centroid(<any>d);
        const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        pos[0] += midAngle < Math.PI ? 5 : -10;
        pos[1] -= 8 * Math.cos(midAngle);
        return 'translate(' + pos + ')';
      })
      .style('display', (d: d3.PieArcDatum<ChartData>): string => this.calcLabelDisplayValue(d))
      .style('text-anchor', (d: d3.PieArcDatum<ChartData>) => {
        const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        return midAngle < Math.PI ? 'start' : 'end';
      });

    // draw label Lines
    this.gElement!.append('g')
      .attr('class', 'donut-label-lines')
      .selectAll('polyline')
      .data(this.data)
      .enter()
      .append('polyline')
      .attr('points', (d: any): any => {
        return [[donutArc.centroid(d)], labelArc.centroid(d)];
      })
      .style('display', (d: d3.PieArcDatum<ChartData>): string => this.calcLabelDisplayValue(d));
  }

  private calcLabelDisplayValue(d: d3.PieArcDatum<ChartData>): string {
    if (d.data.isTransparent) {
      return 'none';
    }
    // only if more then 5 % of the pie
    return d.endAngle - d.startAngle < Math.PI * 2 * 0.05 ? 'none' : 'inherit';
  }

  private drawDonut(
    donutArc: d3.Arc<any, d3.DefaultArcObject>,
    highlightArc: d3.Arc<any, d3.DefaultArcObject>,
    radius: number
  ): void {
    // create donut
    this.gElement!.append('g')
      .attr('class', 'donut')
      .selectAll('path')
      .data(this.data)
      .enter()
      .append('path')
      .style('fill', (d: any) => (d.data.isTransparent ? 'none' : d.data.color))
      .attr('class', (d: any) => {
        return 'donut-slice chart-color-' + (d.data.isTransparent ? 'transparent' : d.index);
      })
      .attr('d', <null>(<unknown>donutArc))
      .on('mouseenter', (_e, d: d3.PieArcDatum<ChartData>) => {
        if (d.data.isTransparent) {
          // transparent # slice has no tooltip
          return;
        }

        const rad = (d.startAngle + d.endAngle) / 2;

        const left = this.svgHTMLElement!.getBoundingClientRect().width / 2 + Math.sin(rad) * radius * 0.6;
        const top = this.svgHTMLElement!.getBoundingClientRect().height / 2 - Math.cos(rad) * radius * 0.6;

        this.gElement!.selectAll('.highlight-slice').filter(`.chart-color-${d.index}`).style('visibility', 'visible');

        this.tooltipContent!.setData(this.initTooltip(d.index, d.data));

        this.tooltipContent!.open(left, top);
      })
      .on('mouseleave', (_e, d: d3.PieArcDatum<ChartData>) => {
        this.gElement!.selectAll('.highlight-slice').filter(`.chart-color-${d.index}`).style('visibility', 'hidden');
        this.tooltipContent!.close();
      });

    // create "highlight" donut which is an outer ring around the main donut
    this.gElement!.append('g')
      .attr('class', 'donut')
      .selectAll('path')
      .data(this.data)
      .enter()
      .append('path')
      .style('fill', (d: any) => d.data.color)
      .attr('class', (d: any) => {
        return 'donut-slice highlight-slice chart-color-' + (d.data.isTransparent ? 'transparent' : d.index);
      })
      .style('visibility', 'hidden')
      .attr('d', <null>(<unknown>highlightArc));
  }

  public render(el: HTMLElement) {
    // el is basically a HTMLELement
    this.svgElement = d3
      .select(el)
      .append('svg')
      .attr(
        'class',
        'mnb-chart mnb-pie-chart donut-chart ' +
          this.config.svgClassName +
          (this.config.style.multiColor ? ' mnb-multi-color-chart' : '')
      )
      .attr('width', '100%')
      .attr('height', '100%');

    this.svgHTMLElement = this.svgElement.node()!;

    const dimensions: { height: number; width: number } = ChartRenderer.getDimensions(el);
    const width = dimensions.width - this.paddingForLabels;
    const height = dimensions.height;
    const radius = Math.min(width, height) / 2;
    const maxDonutWidth = 80;
    const donutArc = d3
      .arc()
      .outerRadius(radius * 0.85)
      .innerRadius(Math.max(radius * 0.85 - maxDonutWidth, radius * 0.4))
      .cornerRadius(0);
    const highlightArc = d3
      .arc()
      .outerRadius(radius * 0.9)
      .innerRadius(radius * 0.85)
      .cornerRadius(0);

    this.gElement = this.svgElement
      .append('g')
      .attr('transform', `translate(${(width + this.paddingForLabels) / 2},${height / 2})`);

    // draw donut
    this.drawDonut(donutArc, highlightArc, radius);
    // draw labels with lines
    if (!this.config.xAxisConfig.hide && !this.config.yAxisConfig.hide) {
      this.drawLabelsWithLines(radius, donutArc);
    }
  }

  private initTooltip(index: number, d: ChartData) {
    const unit = this.config.value!.unit;
    const unitInfo: MeasureUnitInfo | undefined = typeof unit === 'string' ? undefined : unit;
    const unitCode = unitInfo?.code ?? (unit as MeasureUnitCode);
    const tooltipUnit = unitInfo ?? { code: unitCode };
    const tooltipData: ChartTooltipData = {
      index,
      label: d.label,
      seriesData:
        this.config.data?.map(dataArray => {
          const chartData = dataArray.data[index];

          return {
            label: dataArray.label,
            dataLabel: chartData?.label,
            value: chartData?.value,
            seriesColor: dataArray.color ?? chartData?.color,
            includeInTooltip: dataArray.label.trim().length > 0,
            isComparison: dataArray.isComparison,
            isOther: dataArray.isOther,
            unit: tooltipUnit,
          };
        }) ?? [],
    };

    tooltipData.withShares = true;
    tooltipData.values = {
      unit: tooltipUnit,
    };
    const dataArray = this.config.data![0];

    tooltipData.headerData = {
      label: d.label!,
      value: this.config.total,
      sum: dataArray.data.map(d => d.value!).reduce((a, b) => a + b, 0),
    };

    tooltipData.data = {
      label: this.config.value!.label!,
      value: d.value!,
    };

    if (this.config.data!.length > 1) {
      tooltipData.withComparison = true;
      tooltipData.headerData.comparisonSum = this.config.data![1].data.map(d => d.value!).reduce((a, b) => a + b, 0);
      tooltipData.data.comparisonValue = this.config.data![1].data[index].value;
    }

    return tooltipData;
  }
}
